import { useMemo } from 'react'
import { ChevronLeft } from 'lucide-react'
import { Header } from '@components/Header.tsx'
import { useRouter } from '@router'
import type { Recipe } from '@hewey/shared'
import { RecipeCard } from './RecipeCard.tsx'
import styles from './AllRecipesPage.module.css'

type Props = {
	recipes: Recipe[]
	loading: boolean
}

// The full list, A–Z by title, as a dense single column of compact cards.
// Reached from the Explore overview's "View all" button; the header's back
// button returns there.
export const AllRecipesPage = ({ recipes, loading }: Props) => {
	const { navigate } = useRouter()

	const sorted = useMemo(
		() => [...recipes].sort((a, b) => a.title.localeCompare(b.title)),
		[recipes],
	)

	const backButton = (
		<button
			type="button"
			className={styles.back}
			onClick={() => navigate('/recipes')}
			aria-label="Back to overview"
		>
			<ChevronLeft size={22} aria-hidden="true" />
		</button>
	)

	return (
		<main className={styles.screen}>
			<Header
				left={backButton}
				center={<h1 className={styles.title}>All recipes</h1>}
			/>

			{loading && recipes.length === 0 ? (
				<p className={styles.empty}>Loading…</p>
			) : sorted.length === 0 ? (
				<p className={styles.empty}>Nothing yet.</p>
			) : (
				<ul className={styles.list}>
					{sorted.map((r) => (
						<li key={r.id}>
							<RecipeCard
								recipe={r}
								taggingFresh={false}
								compact
							/>
						</li>
					))}
				</ul>
			)}
		</main>
	)
}
